import { getUser, getWeaveSessionId } from '@frontend/auth-helpers';

type FlowState = 'init' | 'in-progress' | 'completed';
type FlowStatus = null | 'success' | 'fail' | 'abandoned';

type Primitive = string | number | boolean | null | undefined;

type FlowEvent = {
  step: string;
  timestamp: number;
  context?: Record<string, Primitive>;
  /** condense: true is magic that tells the flow to condense consecutive events with the same step name into one before reporting */
  options?: { condense?: boolean };
};

type FlowCompleteEventName = 'flow-succeeded' | 'flow-failed' | 'flow-abandoned';

// common data we want to track for every flow
type BaseFlowMetadata = {
  userId: string; // ID of the user performing the flow
  sessionId: string; // Correlate flows within a user session
  userAgent: string; // browser user agent
  screenHeight: number;
  screenWidth: number;
};

export type FlowConfig = Partial<FlowConfigInternal>;

export type FlowConfigInternal = {
  /** is debug logging enabled */
  debug: boolean;
  /** is reporting enabled */
  report: boolean;
  /** minimum time threshold to report the flow (default: 3000ms) */
  reportThresholdMs: number;
  /** if true, flows with no events will not be reported */
  filterOutEmptyFlows: boolean;
  /** callback when flow is completed */
  onComplete?: (flow: Flow) => void;
  /** whether to flush Pendo events on flow completion, helpful if you're about to navigate away */
  flushPendoOnComplete?: boolean;
};

const isLocalDev = typeof window !== 'undefined' && window.location.hostname === 'localhost';

export class Flow<ExtraMetadata extends object = object> {
  readonly id = crypto.randomUUID();
  readonly name: string;
  private state: FlowState = 'init';
  private startedAt: number | null = null;
  private endedAt: number | null = null;
  private events: FlowEvent[] = [];
  private metadata: BaseFlowMetadata & ExtraMetadata;
  private reported = false;
  private config: FlowConfigInternal;

  constructor(name: string, initialMetadata?: Partial<ExtraMetadata>, flowConfig?: FlowConfig) {
    this.name = name;
    this.config = {
      debug: flowConfig?.debug ?? isLocalDev ? true : false,
      report: flowConfig?.report ?? isLocalDev ? false : true,
      reportThresholdMs: flowConfig?.reportThresholdMs ?? 3000,
      filterOutEmptyFlows: flowConfig?.filterOutEmptyFlows ?? true,
      ...flowConfig,
    };
    this.metadata = {
      userId: getUser()?.userID ?? '',
      sessionId: getWeaveSessionId() ?? '',
      userAgent: navigator.userAgent ?? 'unknown',
      screenHeight: window.innerHeight ?? -1,
      screenWidth: window.innerWidth ?? -1,
      ...initialMetadata,
    } as BaseFlowMetadata & ExtraMetadata;
  }

  static create<TExtraMetadata extends object = object>(
    name: string,
    initialMetadata?: Partial<TExtraMetadata>,
    flowConfig?: FlowConfig
  ) {
    return new Flow<TExtraMetadata>(name, initialMetadata, flowConfig);
  }

  static start<TExtraMetadata extends object = object>(
    name: string,
    initialMetadata?: Partial<TExtraMetadata>,
    flowConfig?: FlowConfig
  ) {
    const flow = new Flow<TExtraMetadata>(name, initialMetadata, flowConfig);
    flow.start();
    return flow;
  }

  start(startTime?: number) {
    if (this.state !== 'init') return this;
    this.state = 'in-progress';
    this.startedAt = startTime ?? Date.now();
    this.step('flow-start');
    return this;
  }

  /** Log a step in the flow (for multi-step tracking). Add condense: true to context to condense consecutive steps with the same name */
  step(stepName: string, context?: FlowEvent['context'], options?: { condense?: boolean }) {
    if (this.state !== 'in-progress') this.start(); // auto-start if not already started
    this.events.push({ step: stepName, timestamp: Date.now(), context, options });
    if (this.config.debug) {
      console.log(`[Flow ${this.name}] step: ${stepName}`, context || '', this);
    }
    return this;
  }

  /** Add or update metadata during the flow */
  setMetadata(updates: Partial<ExtraMetadata>) {
    this.metadata = { ...this.metadata, ...updates };
    return this;
  }

  /** Mark the flow as completed in some form */
  complete(status: FlowStatus, context?: FlowEvent['context']) {
    if (this.state !== 'in-progress') return this;
    this.state = 'completed';
    this.step(this.mapFlowStatusToEventName(status), { ...context });
    this.endedAt = Date.now();
    this.report();
    this.config?.onComplete?.(this);
    return this;
  }

  /** convenience method for .complete('abandoned') */
  abandon(context?: FlowEvent['context']) {
    return this.complete('abandoned', context);
  }

  /** convenience method for .complete('success') */
  success(context?: FlowEvent['context']) {
    return this.complete('success', context);
  }

  /** convenience method for .complete('fail') */
  fail(context?: FlowEvent['context']) {
    return this.complete('fail', context);
  }

  private mapFlowStatusToEventName(status: FlowStatus): FlowCompleteEventName {
    switch (status) {
      case 'success':
        return 'flow-succeeded';
      case 'fail':
        return 'flow-failed';
      case 'abandoned':
      default:
        return 'flow-abandoned';
    }
  }

  /** Return the total time elapsed (ms) */
  getElapsedTime(): number {
    if (!this.startedAt || !this.endedAt) return 0;
    return this.endedAt - this.startedAt;
  }

  getEventCount(): number {
    return this.events.length;
  }

  isInProgress() {
    return this.state === 'in-progress';
  }

  isComplete() {
    return this.state === 'completed';
  }

  getFinalStatus() {
    if (!this.isComplete()) return null;

    return this.events.at(-1)?.step === 'flow-succeeded'
      ? 'success'
      : this.events.at(-1)?.step === 'flow-failed'
      ? 'fail'
      : this.events.at(-1)?.step === 'flow-abandoned'
      ? 'abandoned'
      : null;
  }

  /**
   * Pendo has a 1024 char limit on each cell in it's event row table, so when the events array gets too big,
   * we need to condense it by merging consecutive events with the same name that have the condense flag set.
   * This uses a 2-pointers approach and modifies this.events in place
   */
  private condenseDuplicateSteps() {
    if (this.events.length === 0) return;

    const result: FlowEvent[] = [];
    let left = 0;

    while (left < this.events.length) {
      const currentEvent = this.events[left];

      // If this event doesn't have condense flag, just add it and move on
      if (!currentEvent.options?.condense) {
        result.push(currentEvent);
        left++;
        continue;
      }

      // Find the end of consecutive events with same step name and condense flag
      let right = left;
      while (
        right < this.events.length &&
        this.events[right].step === currentEvent.step &&
        this.events[right].options?.condense
      ) {
        right++;
      }

      // Create condensed event from the group [left, right)
      const eventGroup = this.events.slice(left, right);
      result.push(this.createCondensedEvent(eventGroup));

      // Move left pointer to continue processing
      left = right;
    }

    this.events = result;
  }

  private createCondensedEvent(eventGroup: FlowEvent[]): FlowEvent {
    const firstEvent = eventGroup[0];
    const lastEvent = eventGroup[eventGroup.length - 1];

    return {
      ...firstEvent,
      timestamp: firstEvent.timestamp,
      context: {
        ...firstEvent.context,
        eventCount: eventGroup.length,
        ...(eventGroup.length > 1 && { endTimestamp: lastEvent.timestamp }),
      },
    };
  }

  isEmptyFlow(): boolean {
    return this.events.filter((event) => !event.step.includes('flow-')).length === 0;
  }

  report() {
    if (
      this.reported ||
      this.getElapsedTime() < this.config.reportThresholdMs ||
      !this.config.report ||
      (this.config.filterOutEmptyFlows && this.isEmptyFlow())
    ) {
      if (this.config.debug) {
        console.log(`[Flow ${this.name}] skipping report.`);
      }
      return;
    }
    if (this.state !== 'completed') {
      console.warn(`Flow ${this.name} is not completed. Only completed flows can be reported.`);
      return;
    }

    // Condense duplicate steps before reporting
    this.condenseDuplicateSteps();

    this.reported = true;

    // in the future, we'll send elsewhere but using Pendo for now
    this.sendToPendo();
  }

  sendToPendo() {
    const pendoEventName = `flow.${this.name}`;
    // don't report options
    const events = this.events.map((e) => {
      const { options, ...rest } = e;
      return rest;
    });
    const pendoEvent = {
      ...this.metadata,
      flowId: this.id,
      flowName: this.name,
      status: this.getFinalStatus(),
      startedAt: this.startedAt,
      endedAt: this.endedAt,
      elapsedTimeMs: this.getElapsedTime(),
      events: JSON.stringify(events),
      userAgent: this.metadata.userAgent, // report UA to Pendo because they give you a pre-parsed one that is often wrong
      screenHeight: this.metadata.screenHeight,
      screenWidth: this.metadata.screenWidth,
    };
    if (this.config.debug) {
      console.log(`[Flow ${this.name}] reporting to pendo:`, pendoEvent);
    }
    if (this.config.report) {
      if ((globalThis as any).pendo) {
        (globalThis as any).pendo.track(pendoEventName, pendoEvent as any);
        if (this.config.flushPendoOnComplete) {
          (globalThis as any).pendo.flushNow();
        }
      }
    } else if (this.config.debug) {
      console.log(`[Flow ${this.name}] reporting disabled, not sending`);
    }
  }
}
